import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AddressSelector } from '../components/AddressSelector';
import { ResidenceHistory, AddResidenceData } from '../types';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';

export const ProfilePage: React.FC = () => {
  const { user, updateProfile, updateUser } = useAuthStore();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Profile editing states（プロフィール画像編集は無効化）
  
  // Password change states (using reset password flow)
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordStep, setPasswordStep] = useState<'request' | 'verify'>('request');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Residence history states
  const [residenceDialog, setResidenceDialog] = useState(false);
  const [residenceData, setResidenceData] = useState<AddResidenceData>({
    postal_code: '',
    prefecture_code: '',
    city_code: '',
    address: '',
    start_date: '',
    end_date: null,
  });
  const [editingResidence, setEditingResidence] = useState<string | null>(null);

  // ページロード時にプロフィール情報を最新化
  useEffect(() => {
    const refreshProfile = async () => {
      if (user) {
        try {
          console.log('Refreshing profile data...');
          const updatedProfile = await authApi.getProfile();
          console.log('Updated profile from API:', updatedProfile);
          updateUser(updatedProfile);
        } catch (error) {
          console.error('Failed to refresh profile:', error);
        }
      }
    };
    
    refreshProfile();
  }, []); // 初回レンダリング時のみ実行

  // Debug: residence data changes
  useEffect(() => {
    console.log('Residence data changed:', residenceData);
    console.log('Save button enabled:', !loading && !!residenceData.address && !!residenceData.start_date);
  }, [residenceData, loading]);

  // Generate avatar from nickname
  const getAvatarText = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  // Handle password change (using reset password flow)
  const handlePasswordChange = async () => {
    if (!user?.email) {
      setMessage({ type: 'error', text: 'ユーザー情報が取得できません' });
      return;
    }

    if (passwordStep === 'request') {
      // Step 1: Request verification code
      try {
        setLoading(true);
        await authApi.forgotPassword(user.email);
        setMessage({ type: 'success', text: 'パスワード変更用の確認コードをメールに送信しました' });
        setPasswordStep('verify');
      } catch (error) {
        setMessage({ type: 'error', text: '確認コードの送信に失敗しました' });
      } finally {
        setLoading(false);
      }
    } else {
      // Step 2: Reset password with verification code
      if (newPassword !== confirmPassword) {
        setMessage({ type: 'error', text: 'パスワードが一致しません' });
        return;
      }
      
      try {
        setLoading(true);
        await authApi.resetPassword(user.email, verificationCode, newPassword);
        setMessage({ type: 'success', text: 'パスワードを変更しました' });
        setPasswordDialog(false);
        // Reset states
        setPasswordStep('request');
        setVerificationCode('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (error) {
        setMessage({ type: 'error', text: 'パスワードの変更に失敗しました' });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle residence history add/edit
  const handleResidenceSubmit = async () => {
    try {
      setLoading(true);
      console.log('Submitting residence data:', residenceData);
      
      // バリデーションチェック
      const requiredFields = ['address', 'start_date'];
      const missingFields = requiredFields.filter(field => !residenceData[field as keyof typeof residenceData]);
      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        setMessage({ type: 'error', text: `必須項目が不足しています: ${missingFields.join(', ')}` });
        return;
      }
      
      if (editingResidence) {
        console.log('Updating residence history...');
        await authApi.updateResidenceHistory(editingResidence, residenceData);
      } else {
        console.log('Adding residence history...');
        const result = await authApi.addResidenceHistory(residenceData);
        console.log('Add residence result:', result);
      }
      
      setMessage({ type: 'success', text: '居住歴を保存しました' });
      
      // プロフィール情報を再取得
      console.log('Fetching updated profile...');
      const updatedProfile = await authApi.getProfile();
      console.log('Updated profile:', updatedProfile);
      updateUser(updatedProfile);
      
      // 成功後にダイアログを閉じてフォームをリセット
      setResidenceDialog(false);
      resetResidenceForm();
    } catch (error) {
      console.error('Residence submit error:', error);
      setMessage({ type: 'error', text: '居住歴の保存に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  // Reset residence form
  const resetResidenceForm = () => {
    setResidenceData({
      postal_code: '',
      prefecture_code: '',
      city_code: '',
      address: '',
      start_date: '',
      end_date: null,
    });
    setEditingResidence(null);
  };

  // Handle address selection
  const handleAddressSelect = (address: any) => {
    console.log('Selected address:', address);
    setResidenceData(prev => {
      const newData = {
        ...prev,
        postal_code: address.postal_code || '',
        prefecture_code: address.prefecture_code || '',
        city_code: address.city_code || '',
        address: address.address || '',
      };
      console.log('Updated residence data:', newData);
      return newData;
    });
  };

  // Handle residence deletion
  const handleDeleteResidence = async (id: string) => {
    // 確認ダイアログを表示
    if (!window.confirm('この居住歴を削除してもよろしいですか？')) {
      return;
    }
    
    try {
      setLoading(true);
      console.log('Deleting residence history with id:', id);
      await authApi.deleteResidenceHistory(id);
      setMessage({ type: 'success', text: '居住歴を削除しました' });
      // プロフィール情報を再取得
      const updatedProfile = await authApi.getProfile();
      updateUser(updatedProfile);
    } catch (error) {
      console.error('Delete residence error:', error);
      setMessage({ type: 'error', text: '居住歴の削除に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  // Navigate to review creation for specific location
  const handleCreateReview = (residence: ResidenceHistory) => {
    navigate('/reviews/create', {
      state: {
        prefillAddress: {
          postal_code: residence.postal_code,
          prefecture_code: residence.prefecture_code,
          city_code: residence.city_code,
          address: residence.address,
        }
      }
    });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const [year, month] = dateString.split('-');
      if (!year || !month) return dateString;
      return `${year}年${month}月`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString;
    }
  };

  // Safe date formatting for general dates
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return format(date, 'yyyy年MM月dd日');
    } catch (error) {
      console.error('DateTime formatting error:', error);
      return '';
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">ユーザー情報を取得できませんでした</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        プロフィール
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Basic Information Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">基本情報</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box>
              <Box position="relative">
                <Avatar
                  sx={{ width: 80, height: 80, fontSize: '2rem' }}
                  src={user.profile_image_url}
                >
                  {getAvatarText(user.nickname)}
                </Avatar>
                <Chip 
                  label="変更不可" 
                  size="small" 
                  color="default" 
                  variant="outlined"
                  sx={{ 
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '0.6rem'
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ニックネーム
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="h6">{user.nickname}</Typography>
                  <Chip 
                    label="変更不可" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  メールアドレス: {user.email}
                  <Chip 
                    label="変更不可" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  登録日: {formatDateTime(user.created_at)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Residence History Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">居住歴</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setResidenceDialog(true)}
            >
              居住歴を追加
            </Button>
          </Box>
          
          {user.residence_history && user.residence_history.length > 0 ? (
            <List>
              {user.residence_history
                .sort((a, b) => {
                  try {
                    const dateA = new Date(a.start_date);
                    const dateB = new Date(b.start_date);
                    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                      return 0;
                    }
                    return dateB.getTime() - dateA.getTime();
                  } catch (error) {
                    console.error('Date sorting error:', error);
                    return 0;
                  }
                })
                .map((residence) => (
                  <ListItem key={residence.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {residence.address}
                          </Typography>
                          {residence.has_review ? (
                            <Chip label="口コミ投稿済み" size="small" color="success" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ReviewIcon />}
                              onClick={() => handleCreateReview(residence)}
                            >
                              口コミを書く
                            </Button>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(residence.start_date)} 〜 {residence.end_date ? formatDate(residence.end_date) : '現在'}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteResidence(residence.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              居住歴が登録されていません
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Security Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">セキュリティ</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setPasswordDialog(true)}
            >
              パスワードを変更
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              ※ ニックネーム、メールアドレス、プロフィール画像は変更できません。これによりアカウントの整合性が保たれます。
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Residence Dialog */}
      <Dialog
        open={residenceDialog}
        onClose={() => setResidenceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingResidence ? '居住歴を編集' : '居住歴を追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <AddressSelector
              onAddressSelect={handleAddressSelect}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="開始年月"
                  type="month"
                  value={residenceData.start_date}
                  onChange={(e) => setResidenceData(prev => ({ ...prev, start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="終了年月（現在居住中の場合は空白）"
                  type="month"
                  value={residenceData.end_date || ''}
                  onChange={(e) => setResidenceData(prev => ({ ...prev, end_date: e.target.value || null }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResidenceDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleResidenceSubmit}
            variant="contained"
            disabled={loading || !residenceData.address || !residenceData.start_date}
            title={`Debug: address=${!!residenceData.address}, start_date=${!!residenceData.start_date}, loading=${loading}`}
          >
            {loading ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialog}
        onClose={() => {
          setPasswordDialog(false);
          setPasswordStep('request');
          setVerificationCode('');
          setNewPassword('');
          setConfirmPassword('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          パスワード変更 {passwordStep === 'request' ? '- 確認コード送信' : '- 新しいパスワード設定'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {passwordStep === 'request' ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  パスワードを変更するには、確認コードをメールアドレス（{user?.email}）に送信します。
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  「確認コードを送信」ボタンをクリックしてください。
                </Typography>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="確認コード"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="メールで受信した確認コードを入力"
                  helperText="メールアドレスに送信された6桁の確認コードを入力してください"
                />
                <TextField
                  fullWidth
                  label="新しいパスワード"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="8文字以上で入力してください"
                />
                <TextField
                  fullWidth
                  label="新しいパスワード（確認）"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasswordDialog(false);
            setPasswordStep('request');
            setVerificationCode('');
            setNewPassword('');
            setConfirmPassword('');
          }}>
            キャンセル
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={
              loading || 
              (passwordStep === 'verify' && (!verificationCode || !newPassword || !confirmPassword))
            }
          >
            {loading ? <CircularProgress size={20} /> : 
             (passwordStep === 'request' ? '確認コードを送信' : 'パスワードを変更')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
